// @ts-nocheck
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, Users, TrendingUp, Tag, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

interface AffiliateDetailsSheetProps {
  affiliateId: string | null;
  affiliateName: string;
  affiliateEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AffiliateDetailsSheet: React.FC<AffiliateDetailsSheetProps> = ({
  affiliateId,
  affiliateName,
  affiliateEmail,
  open,
  onOpenChange,
}) => {
  // Fetch affiliate data
  const { data: affiliate, isLoading: affiliateLoading } = useQuery({
    queryKey: ['affiliate-details', affiliateId],
    queryFn: async () => {
      if (!affiliateId) return null;
      const { data, error } = await supabase
        .from('affiliates')
        .select('*')
        .eq('id', affiliateId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!affiliateId && open,
  });

  // Fetch profile data
  const { data: profile } = useQuery({
    queryKey: ['affiliate-profile', affiliate?.user_id],
    queryFn: async () => {
      if (!affiliate?.user_id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('name, email')
        .eq('user_id', affiliate.user_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!affiliate?.user_id && open,
  });

  // Fetch performance stats
  const { data: stats } = useQuery({
    queryKey: ['affiliate-stats', affiliateId],
    queryFn: async () => {
      if (!affiliateId) return null;

      // Get conversions count
      const { count: conversionsCount } = await supabase
        .from('affiliate_conversions')
        .select('*', { count: 'exact', head: true })
        .eq('affiliate_id', affiliateId);

      // Get clicks count
      const { count: clicksCount } = await supabase
        .from('affiliate_click_tracking')
        .select('*', { count: 'exact', head: true })
        .eq('affiliate_id', affiliateId);

      // Get active coupons count
      const { count: couponsCount } = await supabase
        .from('affiliate_coupon_codes')
        .select('*', { count: 'exact', head: true })
        .eq('affiliate_id', affiliateId)
        .eq('is_active', true);

      return {
        conversions: conversionsCount || 0,
        clicks: clicksCount || 0,
        coupons: couponsCount || 0,
      };
    },
    enabled: !!affiliateId && open,
  });

  // Fetch earnings breakdown
  const { data: earnings } = useQuery({
    queryKey: ['affiliate-earnings', affiliateId],
    queryFn: async () => {
      if (!affiliateId) return null;

      const { data, error } = await supabase
        .from('affiliate_earnings')
        .select('amount, status')
        .eq('affiliate_id', affiliateId);

      if (error) throw error;

      const pending = data?.filter(e => e.status === 'pending').reduce((sum, e) => sum + (e.amount || 0), 0) || 0;
      const paid = data?.filter(e => e.status === 'paid').reduce((sum, e) => sum + (e.amount || 0), 0) || 0;

      return { pending, paid };
    },
    enabled: !!affiliateId && open,
  });

  // Fetch recent earnings
  const { data: recentEarnings } = useQuery({
    queryKey: ['affiliate-recent-earnings', affiliateId],
    queryFn: async () => {
      if (!affiliateId) return null;

      const { data, error } = await supabase
        .from('affiliate_earnings')
        .select('*')
        .eq('affiliate_id', affiliateId)
        .order('earned_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
    enabled: !!affiliateId && open,
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; icon: React.ReactNode }> = {
      active: { variant: 'default', icon: <CheckCircle className="w-3 h-3 mr-1" /> },
      pending: { variant: 'secondary', icon: <Clock className="w-3 h-3 mr-1" /> },
      suspended: { variant: 'destructive', icon: <AlertCircle className="w-3 h-3 mr-1" /> },
      rejected: { variant: 'secondary', icon: <XCircle className="w-3 h-3 mr-1" /> },
    };

    const config = variants[status] || variants.pending;

    return (
      <Badge variant={config.variant} className="flex items-center w-fit">
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (affiliateLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!affiliate) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{profile?.name || affiliateName}</SheetTitle>
          <SheetDescription>{profile?.email || affiliateEmail}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(affiliate.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Join Date</p>
                  <p className="text-sm font-medium mt-1">
                    {format(new Date(affiliate.created_at), 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Referral Code</p>
                <code className="mt-1 block px-3 py-2 bg-muted rounded-md text-sm font-mono">
                  {affiliate.referral_code}
                </code>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Commission Rate</p>
                  <p className="text-lg font-bold text-primary mt-1">
                    {affiliate.commission_rate}%
                  </p>
                </div>
                {affiliate.override_rate && (
                  <div>
                    <p className="text-sm text-muted-foreground">Override Rate</p>
                    <p className="text-lg font-bold text-primary mt-1">
                      {affiliate.override_rate}%
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stripe Connect Status */}
          <Card>
            <CardHeader>
              <CardTitle>Stripe Connect Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Account ID</p>
                <p className="text-sm font-mono mt-1">
                  {affiliate.stripe_account_id || (
                    <span className="text-muted-foreground italic">Not connected</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="mt-1">
                  {affiliate.stripe_account_status ? (
                    <Badge variant={affiliate.stripe_account_status === 'complete' ? 'default' : 'secondary'}>
                      {affiliate.stripe_account_status}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not Connected</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                  <div className="p-2 rounded-full bg-primary/10">
                    <DollarSign className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Earnings</p>
                    <p className="text-2xl font-bold">
                      ${(affiliate.total_earnings || 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                  <div className="p-2 rounded-full bg-primary/10">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Conversions</p>
                    <p className="text-2xl font-bold">{stats?.conversions || 0}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Clicks</p>
                    <p className="text-2xl font-bold">{stats?.clicks || 0}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Tag className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active Coupons</p>
                    <p className="text-2xl font-bold">{stats?.coupons || 0}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Earnings Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Earnings Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-sm text-muted-foreground mb-1">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-500">
                    ${(earnings?.pending || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-sm text-muted-foreground mb-1">Paid</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-500">
                    ${(earnings?.paid || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Earnings */}
          {recentEarnings && recentEarnings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Earnings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentEarnings.map((earning) => (
                    <div
                      key={earning.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div>
                        <p className="text-sm font-medium">
                          {earning.source_type.replace('_', ' ').toUpperCase()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(earning.earned_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">
                          ${earning.amount.toFixed(2)}
                        </p>
                        <Badge
                          variant={earning.status === 'paid' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {earning.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
